import React, { useMemo, useState } from "react";
import { Card, Col, Row, Button, Form, Image, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Shoporder } from "Components/ShopTopBar";
import DeleteModal from "Components/DeleteModal";

// cart api
import {
  useGetStoreCartQuery,
  useUpdateCartItemQtyMutation,
  useRemoveCartItemMutation,
} from "../../redux/services/store/storeCartApi";

const toNum = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const parseQty = (v: any): number => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 1;
  return Math.max(1, Math.round(n));
};

const getCustomizationSummary = (item: any) => {
  const color = String(item?.customization_color || "").trim();
  const note = String(item?.customization_note || "").trim();
  const enabled = Boolean(item?.customization_enabled || color || note);
  if (!enabled) return "";

  const parts = [];
  if (color) parts.push(`Custom color: ${color}`);
  if (note) parts.push(`Custom note: ${note}`);
  return parts.length ? parts.join(" | ") : "Customization requested";
};

const Cardshop = () => {
  const { data, isLoading, isFetching, isError } = useGetStoreCartQuery();

  const [updateQty, { isLoading: isQtyLoading }] = useUpdateCartItemQtyMutation();
  const [removeItem, { isLoading: isRemoveLoading }] = useRemoveCartItemMutation();

  const [id, setId] = useState<number | null>(null);
  const [removeModel, setRemovemodel] = useState(false);

  const RemoveModel = (itemId?: any) => {
    if (itemId !== undefined && itemId !== null) {
      setId(Number(itemId));
      setRemovemodel(true);
      return;
    }
    setRemovemodel(false);
  };

  const busy = isLoading || isFetching || isQtyLoading || isRemoveLoading;

  const items = (data as any)?.items ?? [];
  const currency = (data as any)?.cart?.currency_code || "USD";

  const subtotal = useMemo(() => toNum((data as any)?.totals?.subtotal), [data]);
  const discount = useMemo(() => toNum((data as any)?.totals?.discount), [data]);
  const tax = useMemo(() => toNum((data as any)?.totals?.tax), [data]);

  const charge = useMemo(() => {
    const backendShipping = (data as any)?.totals?.shipping;
    if (backendShipping != null) return toNum(backendShipping);
    return 0;
  }, [data]);

  const total = useMemo(() => {
    const backendTotal = (data as any)?.totals?.total;
    if (backendTotal != null) return toNum(backendTotal);
    return subtotal + charge + tax - discount;
  }, [data, subtotal, charge, tax, discount]);

  const countUP = async (it: any) => {
    const current = parseQty(it?.qty);
    const next = Math.min(100, current + 1);
    try {
      await updateQty({ id: it.id, qty: next }).unwrap();
    } catch (e) {
      console.log(e);
    }
  };

  const countDown = async (it: any) => {
    const current = parseQty(it?.qty);
    const next = Math.max(1, current - 1);
    try {
      await updateQty({ id: it.id, qty: next }).unwrap();
    } catch (e) {
      console.log(e);
    }
  };

  const deleteData = async () => {
    if (!id) return;
    try {
      await removeItem({ id }).unwrap();
      setRemovemodel(false);
      setId(null);
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <React.Fragment>
      <Col lg={8}>
        <div className="d-flex align-items-center mb-4">
          <h5 className="mb-0 flex-grow-1 fw-medium">
            There are <span className="fw-bold product-count">{items.length}</span> products in your cart
          </h5>
          <div className="flex-shrink-0">
            <Link
              to="#"
              className="text-decoration-underline link-secondary"
              onClick={(e) => {
                e.preventDefault();
                console.log("Clear Cart not implemented on backend yet");
              }}
            >
              Clear Cart
            </Link>
          </div>
        </div>

        {isError ? (
          <Card className="product">
            <Card.Body className="p-4 text-danger">Failed to load cart</Card.Body>
          </Card>
        ) : isLoading ? (
          <Card className="product">
            <Card.Body className="p-4 text-center">
              <Spinner animation="border" size="sm" /> Loading...
            </Card.Body>
          </Card>
        ) : (
          (items || []).map((it: any, inx: number) => {
            const p = it?.product || {};
            const title = p?.name || "";
            const img = p?.image?.cdn_url || "https://via.placeholder.com/96x96.png?text=IMG";

            const attrs = (p?.attributes_json || {}) as Record<string, any>;
            const Color =
              attrs.color ||
              attrs.Color ||
              attrs.colour ||
              attrs.Colour ||
              attrs.color_name ||
              attrs.color_swatch ||
              "";
            const Size = attrs.size || attrs.Size || "";

            const qty = parseQty(it?.qty);
            const itemPrice = toNum(it?.unit_price);
            const compareAtPrice = toNum(it?.compare_at_price);
            const discountAmount = toNum(it?.discount_amount);
            const discountPercent = toNum(it?.discount_percent);
            const hasDiscount = compareAtPrice > itemPrice && discountAmount > 0;
            const lineTotal = it?.line_total != null ? toNum(it?.line_total) : itemPrice * qty;
            const customizationSummary = getCustomizationSummary(it);

            return (
              <Card key={it?.id ?? inx} className="product">
                <Card.Body className="p-4">
                  <Row className="gy-3">
                    <Col className="col-sm-auto">
                      <div className="avatar-lg h-100">
                        <div className="avatar-title bg-light-subtle rounded py-3">
                          <Image src={img} alt="" className="avatar-md" />
                        </div>
                      </div>
                    </Col>

                    <Col className="col-sm">
                      <Link to={`/product/${p?.slug || ""}`}>
                        <h5 className="fs-16 lh-base mb-1">{title}</h5>
                      </Link>

                      <ul className="list-inline text-muted fs-13 mb-3">
                        <li className="list-inline-item">
                          Color : <span className="fw-medium">{Color || "-"}</span>
                        </li>
                        {Size ? (
                          <li className="list-inline-item">
                            Size : <span className="fw-medium">{Size}</span>
                          </li>
                        ) : null}
                      </ul>

                      {customizationSummary ? (
                        <p className="text-muted fs-13 mb-3">{customizationSummary}</p>
                      ) : null}

                      <div className="input-step">
                        <Button className="minus" onClick={() => countDown(it)} disabled={busy}>
                          –
                        </Button>
                        <Form.Control
                          type="number"
                          className="product-quantity"
                          value={qty}
                          min="1"
                          max="100"
                          readOnly
                        />
                        <Button className="plus" onClick={() => countUP(it)} disabled={busy}>
                          +
                        </Button>
                      </div>
                    </Col>

                    <Col className="col-sm-auto">
                      <div className="text-lg-end">
                        <p className="text-muted mb-1 fs-12">Item Price:</p>
                        <h5 className="fs-16">
                          {currency}
                          <span className="product-price"> {itemPrice.toFixed(2)}</span>
                        </h5>
                        {hasDiscount ? (
                          <>
                            <div className="text-muted text-decoration-line-through fs-13">
                              {currency} {compareAtPrice.toFixed(2)}
                            </div>
                            <div className="text-success fs-12 fw-semibold">
                              Save {discountPercent > 0 ? `${Math.round(discountPercent)}%` : `${currency} ${discountAmount.toFixed(2)}`}
                            </div>
                          </>
                        ) : null}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>

                <Card.Footer>
                  <Row className="align-items-center gy-3">
                    <Col className="col-sm">
                      <div className="d-flex flex-wrap my-n1">
                        <div>
                          <Link
                            to="#"
                            className="d-block text-body p-1 px-2"
                            data-bs-toggle="modal"
                            data-bs-target="#removeItemModal"
                            onClick={(e) => {
                              e.preventDefault();
                              RemoveModel(it.id);
                            }}
                          >
                            <i className="ri-delete-bin-fill text-muted align-bottom me-1"></i> Remove
                          </Link>
                        </div>
                        <div>
                          <Link to="#" className="d-block text-body p-1 px-2" onClick={(e) => e.preventDefault()}>
                            <i className="ri-star-fill text-muted align-bottom me-1"></i> Add Wishlist
                          </Link>
                        </div>
                      </div>
                    </Col>

                    <Col className="col-sm-auto">
                      <div className="d-flex align-items-center gap-2 text-muted">
                        <div>Total :</div>
                        <div className="text-end">
                          <h5 className="fs-14 mb-0">
                            {currency}
                            <span className="product-line-price"> {lineTotal.toFixed(2)}</span>
                          </h5>
                          {hasDiscount ? (
                            <small className="text-success d-block">
                              Saved {currency} {discountAmount.toFixed(2)}
                            </small>
                          ) : null}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Footer>
              </Card>
            );
          })
        )}
      </Col>

      <div className="col-xl-4">
        <div className="sticky-side-div">
          <Shoporder
            subtotal={subtotal}
            dic={discount}
            charge={charge}
            tax={tax}
            total={total}
            currency={currency}
          />

          <div className="hstack gap-2 justify-content-end">
            <Button
              variant="danger"
              className="btn btn-hover"
              onClick={() => (window.location.href = "/")}
              disabled={busy}
            >
              Continue Shopping
            </Button>
            <Button
              variant="success"
              className="btn btn-hover"
              onClick={() => (window.location.href = "/shop/checkout")}
              disabled={busy}
            >
              Check Out <i className="ri-logout-box-r-line align-bottom ms-1"></i>
            </Button>
          </div>
        </div>
      </div>

      <DeleteModal removeModel={removeModel} hideModal={RemoveModel} deleteData={deleteData} />
    </React.Fragment>
  );
};

export default Cardshop;
